import { Link, useLocation } from 'react-router'

import Card, { CardContent } from 'js/components/Card'
import CoverImage from 'js/components/CoverImage'
import { DisplayDate } from 'js/components/DisplayDate'
import FeedItem from 'js/components/FeedItem/FeedItem'
import ActionsButton from 'js/magazine-issue/components/ActionsButton'
import ThemeProvider from 'js/theme/ThemeProvider'

import styles from './MagazineIssueCard.module.css'

type MagazineIssueCardWrapperProps = {
    showTheme?: boolean
    themeColor?: string
    children?: React.ReactNode
} & Record<string, unknown>

const MagazineIssueCardWrapper = ({
    showTheme,
    themeColor,
    children,
    ...rest
}: MagazineIssueCardWrapperProps) => {
    return showTheme ? (
        <ThemeProvider
            theme={themeColor}
            style={{ display: 'flex', flexDirection: 'column' }} // Fill out vertically
        >
            <Card className={styles.wrapper} {...rest}>
                {children}
            </Card>
        </ThemeProvider>
    ) : (
        <FeedItem className={styles.wrapper} {...rest}>
            {children}
        </FeedItem>
    )
}

type IssueCardEntity = {
    guid: string
    url: string
    issueNumber: string
    timePublished?: string
    articles: Array<{
        guid: string
        title?: string
        backgroundColor?: string
        featured?: boolean
    }>
    container?: { title?: string }
    backgroundColor?: string
}

type IssueCardProps = {
    entity: IssueCardEntity
    showTheme?: boolean
    dataFeed?: string
}

const MagazineIssueCard = ({ entity, showTheme, dataFeed }: IssueCardProps) => {
    const location = useLocation()

    const { url, issueNumber, timePublished, articles, container } = entity

    const featuredArticle = articles[0]

    const { featured } = featuredArticle || {}

    const backgroundColor =
        entity.backgroundColor || featuredArticle?.backgroundColor

    const themeColor = backgroundColor || '#FFFFFF'

    const title = showTheme ? issueNumber : `${container?.title} ${issueNumber}`

    return (
        <MagazineIssueCardWrapper
            showTheme={showTheme}
            themeColor={themeColor}
            data-feed={dataFeed}
        >
            <CardContent>
                <CoverImage
                    url={url}
                    hideCaption
                    featured={featured}
                    size="landscape-large"
                    className={styles.featuredImage}
                />

                <div className={styles.meta}>
                    <Link
                        to={url}
                        state={{
                            prevPathname: location.pathname,
                        }}
                        className={styles.title}
                    >
                        {title}
                    </Link>
                    <ActionsButton entity={entity} />
                </div>
                <DisplayDate
                    date={timePublished}
                    type="date"
                    disableTooltip
                    size="small"
                />

                {articles.length ? (
                    <ul className={styles.tableOfContents}>
                        {articles.slice(0, 3).map(({ guid, title }) => (
                            <li key={guid}>{title}</li>
                        ))}
                    </ul>
                ) : null}
            </CardContent>
        </MagazineIssueCardWrapper>
    )
}

export default MagazineIssueCard
